/**
 * Infinite scroll / load-more for collection product grids.
 *
 * Modes are chosen in the theme customizer via the section setting
 * `pagination_type` on Main collection product grid:
 *   - infinite_scroll : sentinel near the bottom of the grid auto-loads the
 *                        next page only once the user actually scrolls it
 *                        into view (no auto-load on initial page mount).
 *   - click_load_more : a button fetches and appends the next page on click.
 *   - pagination      : (default) stock Dawn numbered pagination, this
 *                        script does nothing in that mode.
 *
 * Must keep working after FacetFiltersForm (facets.js) replaces
 * #ProductGridContainer's innerHTML on every filter/sort change.
 * window.initInfiniteScroll() is safe to call repeatedly — it always tears
 * down any previous observer/listener before attaching new ones, and
 * facets.js calls it again after every re-render.
 */
(function () {
  var DEBUG = true; // set to false once you've confirmed it's working
  function log() {
    if (DEBUG && window.console) console.log.apply(console, ['[infinite-scroll]'].concat([].slice.call(arguments)));
  }

  var currentObserver = null;
  var currentScrollHandler = null;

  function getSectionId(container) {
    var grid = container.querySelector('#product-grid');
    return grid ? grid.dataset.id : null;
  }

  function buildFetchUrl(nextUrl, sectionId) {
    var url = new URL(nextUrl, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  }

  function setLoading(wrapper, isLoading) {
    if (!wrapper) return;
    var spinner = wrapper.querySelector('.infinite-scroll__spinner');
    var button = wrapper.querySelector('.load-more__btn');
    if (spinner) spinner.classList.toggle('hidden', !isLoading);
    if (button) button.classList.toggle('hidden', isLoading);
    wrapper.dataset.loading = isLoading ? 'true' : 'false';
  }

  function fetchAndAppend(container, nextUrl, wrapper) {
    if (!nextUrl || wrapper.dataset.loading === 'true') return;

    var sectionId = getSectionId(container);
    if (!sectionId) {
      log('no section id found, aborting fetch');
      return;
    }

    log('loading next page:', nextUrl);
    setLoading(wrapper, true);

    fetch(buildFetchUrl(nextUrl, sectionId))
      .then(function (response) {
        return response.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var newGrid = parsed.getElementById('product-grid');
        var liveGrid = container.querySelector('#product-grid');

        if (newGrid && liveGrid) {
          Array.prototype.slice.call(newGrid.children).forEach(function (item) {
            liveGrid.appendChild(item);
          });
          log('appended', newGrid.children.length, 'items');
        } else {
          log('could not find product-grid in fetched HTML or live DOM');
        }

        var newWrapper = parsed.querySelector('.infinite-scroll-wrapper');
        var mountPoint =
          container.querySelector('#ProductGridContainer') ||
          container.querySelector('.product-grid-container') ||
          container;

        wrapper.remove();
        if (newWrapper) {
          mountPoint.appendChild(newWrapper);
        }

        // Re-attach for the newly appended sentinel/button. This does NOT
        // trigger another auto-load by itself — see the "ignore the first
        // report" guard below.
        window.initInfiniteScroll(container);
      })
      .catch(function (error) {
        console.error('[infinite-scroll] fetch failed:', error);
        setLoading(wrapper, false);
      });
  }

  window.initInfiniteScroll = function initInfiniteScroll(root) {
    var container = root && root.nodeType ? root : document;

    if (currentObserver) {
      currentObserver.disconnect();
      currentObserver = null;
    }
    if (currentScrollHandler) {
      window.removeEventListener('scroll', currentScrollHandler);
      window.removeEventListener('resize', currentScrollHandler);
      currentScrollHandler = null;
    }

    var wrapper = container.querySelector('.infinite-scroll-wrapper');
    if (!wrapper) {
      log('no .infinite-scroll-wrapper found in', container === document ? 'document' : container.id || container);
      return;
    }

    var mode = wrapper.dataset.mode;
    log('initializing mode:', mode);

    if (mode === 'click') {
      var button = wrapper.querySelector('.load-more__btn');
      if (!button) return;
      button.addEventListener('click', function () {
        fetchAndAppend(container, button.dataset.nextUrl, wrapper);
      });
      return;
    }

    if (mode === 'scroll') {
      var trigger = wrapper.querySelector('.infinite-scroll-trigger');
      if (!trigger) {
        log('scroll mode but no .infinite-scroll-trigger found');
        return;
      }

      // IntersectionObserver reports the CURRENT state immediately as soon
      // as observe() is called — that's spec behaviour, not a bug — which
      // means the very first callback fires before any real scrolling has
      // happened. We deliberately ignore that first report so the grid
      // never auto-loads on mount; only genuine, later intersection changes
      // (i.e. actual scrolling) trigger a fetch.
      var ignoredInitialReport = false;

      if ('IntersectionObserver' in window) {
        currentObserver = new IntersectionObserver(
          function (entries) {
            if (!ignoredInitialReport) {
              ignoredInitialReport = true;
              log('ignoring initial intersection report (no scroll yet)');
              return;
            }
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                log('trigger intersected viewport after scrolling');
                fetchAndAppend(container, trigger.dataset.nextUrl, wrapper);
              }
            });
          },
          { rootMargin: '0px 0px 200px 0px', threshold: 0 }
        );
        currentObserver.observe(trigger);
      }

      // Backup mechanism only — reacts to real scroll/resize events, never
      // fires on its own during setup.
      currentScrollHandler = function () {
        var rect = trigger.getBoundingClientRect();
        var nearViewport = rect.top <= window.innerHeight + 200;
        if (nearViewport) {
          fetchAndAppend(container, trigger.dataset.nextUrl, wrapper);
        }
      };
      window.addEventListener('scroll', currentScrollHandler, { passive: true });
      window.addEventListener('resize', currentScrollHandler);
    }
  };

  function boot() {
    log('booting, calling initInfiniteScroll(document)');
    window.initInfiniteScroll(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();